<script setup lang="ts">
import { ref, computed } from 'vue'
import { useTimer } from '@/shared/composables/useTimer'
import type { CardioType } from '../lib/types'
import { isFieldEntered, SCORING_FIELD_PLACEHOLDER } from '../lib/scoringInput'

const type     = defineModel<CardioType>('type',     { required: true })
const runMin   = defineModel<string>('runMin',       { required: true })
const runSec   = defineModel<string>('runSec',       { required: true })
const shuttles = defineModel<string>('shuttles',     { required: true })
const exempt   = defineModel<boolean>('exempt',      { required: true })

const props = defineProps<{ componentScore: number | null }>()

const isOpen = ref(false)
const isRun  = computed(() => type.value === 'run')
const { startStopwatch } = useTimer()

const hasInput = computed(() => {
  if (isRun.value) return isFieldEntered(runMin.value) || isFieldEntered(runSec.value)
  return isFieldEntered(shuttles.value)
})

const pillText = computed(() => {
  if (exempt.value) return 'EXEMPT'
  if (!hasInput.value) return '–'
  if (props.componentScore !== null) return props.componentScore.toFixed(1) + ' pts'
  return '–'
})
</script>

<template>
  <div class="acc-card">
    <div class="acc-header" @click="isOpen = !isOpen">
      <div class="acc-header-left">
        <span class="acc-title">Cardio</span>
        <span class="acc-pts">max 50 pts</span>
      </div>
      <label class="acc-exempt" @click.stop>
        <input type="checkbox" v-model="exempt" style="accent-color:var(--accent);width:14px;height:14px;" />
        Exempt
      </label>
      <span class="acc-score-pill" :class="{ 'has-score': !exempt && hasInput && componentScore !== null }">
        {{ pillText }}
      </span>
      <span class="acc-tap-hint">{{ isOpen ? 'tap to collapse' : 'tap to expand' }}</span>
      <span class="acc-chevron" :class="{ open: isOpen }" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="m9 18 6-6-6-6" />
        </svg>
      </span>
    </div>

    <div v-show="isOpen" class="acc-body">
      <div :style="exempt ? { opacity: '0.35', pointerEvents: 'none' } : {}">
        <div class="row">
          <div class="field">
            <label>Type</label>
            <div class="toggle-group">
              <button :class="['toggle-btn', { active: type === 'run' }]"  @click="type = 'run'">2-Mile Run</button>
              <button :class="['toggle-btn', { active: type === 'hamr' }]" @click="type = 'hamr'">HAMR Shuttle</button>
            </div>
          </div>
          <div v-if="!isRun" class="field sm">
            <label>Shuttles</label>
            <input type="text" inputmode="numeric" :placeholder="SCORING_FIELD_PLACEHOLDER" v-model="shuttles" aria-label="HAMR shuttles" />
          </div>
        </div>

        <div v-if="!isRun && !exempt" style="margin-top:10px;background:#0f1318;border:1px solid #2a3540;border-radius:8px;padding:12px 14px;">
          <div style="font-family:'Barlow Condensed',sans-serif;font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--accent);margin-bottom:8px;">
            HAMR Audio - Official 20m Beep Test
          </div>
          <audio controls preload="none"
            src="assets/audio/air-force-20m-hamr-audio-file.mp3"
            style="width:100%;height:36px;accent-color:var(--accent);">
            Your browser does not support audio.
          </audio>
          <div style="font-size:11px;color:#445;margin-top:6px;">Enter your shuttle count above after stopping.</div>
        </div>

        <div v-if="isRun" style="display:flex;gap:12px;margin-top:4px;">
          <div class="field sm" style="flex:1;">
            <label>Minutes</label>
            <input type="text" inputmode="numeric" :placeholder="SCORING_FIELD_PLACEHOLDER" v-model="runMin" aria-label="Run minutes" />
          </div>
          <div class="field sm" style="flex:1;">
            <label>Seconds</label>
            <input type="text" inputmode="numeric" :placeholder="SCORING_FIELD_PLACEHOLDER" v-model="runSec" aria-label="Run seconds" />
          </div>
        </div>

        <div v-if="isRun && !exempt" style="margin-top:10px;">
          <button class="calc-timer-btn" @click="startStopwatch('2-Mile Run', 'run')">Start Run Timer</button>
        </div>
      </div>
    </div>
  </div>
</template>
